/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useRef, useEffect } from 'react';
import { EmbeddedWalletService } from '../lib/embedded-wallets';

export const useEmailAuth = (onSuccess: (email: string) => Promise<void>) => {
    const [step, setStep] = useState<'EMAIL' | 'OTP'>('EMAIL');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Timer state
    const [cooldown, setCooldown] = useState(0);
    const timerRef = useRef<any>(null);

    useEffect(() => {
        if (cooldown > 0) {
            timerRef.current = setTimeout(() => setCooldown(c => c - 1), 1000);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [cooldown]);

    const handleSendCode = async (emailInput: string) => {
        setIsLoading(true);
        setError(null);
        try {
            if (!emailInput.includes('@')) throw new Error("Invalid email address");
            
            const sent = await EmbeddedWalletService.sendOtp(emailInput);
            if (sent) {
                setEmail(emailInput);
                setStep('OTP');
                setCooldown(30); // 30s resend cooldown
            } else {
                throw new Error("Failed to send code");
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (code: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const isValid = await EmbeddedWalletService.verifyOtp(email, code);
            if (isValid) {
                await onSuccess(email);
            } else {
                throw new Error("Invalid code. Please try again.");
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (cooldown > 0) return;
        await handleSendCode(email);
    };

    const reset = () => {
        setStep('EMAIL');
        setEmail('');
        setOtp('');
        setError(null);
    };

    return {
        step,
        email,
        otp,
        setOtp,
        isLoading,
        error,
        cooldown,
        handleSendCode,
        handleVerifyCode,
        handleResend,
        reset
    };
};